import {
  AbstractControl,
  FormControl,
  FormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { VxKeyValuePair } from '@ui/kv-editor/kv-editor.types';
import {
  AlertCorrelatorType,
  AlertProcessorConfiguration,
  AlertProcessorConfigurationSpec,
} from 'src/app/models/alert';

/**
 * The processor configuration form, shared by the create and details pages
 * so both agree on shape, defaults, validation and the spec they send.
 */
export type AlertProcessorConfigurationForm = FormGroup<{
  autoCreateIncidents: FormControl<boolean>;
  correlatorType: FormControl<AlertCorrelatorType>;
  similarityThreshold: FormControl<number>;
  weights: FormControl<VxKeyValuePair[]>;
}>;

const DEFAULT_SIMILARITY_THRESHOLD = 0.7;

/** Every weight row needs a label and an integer between 0 and 100. */
export function weightsValidator(
  control: AbstractControl
): ValidationErrors | null {
  const rows: VxKeyValuePair[] = control.value ?? [];
  const valid = rows.every(row => {
    const weight = Number(row.value);
    return (
      row.key.trim() !== '' &&
      row.value.trim() !== '' &&
      Number.isInteger(weight) &&
      weight >= 0 &&
      weight <= 100
    );
  });
  return valid ? null : { invalidWeight: true };
}

export function buildAlertProcessorConfigurationForm(): AlertProcessorConfigurationForm {
  return new FormGroup({
    autoCreateIncidents: new FormControl(true, { nonNullable: true }),
    correlatorType: new FormControl<AlertCorrelatorType>(
      AlertCorrelatorType.STATISTICAL,
      {
        nonNullable: true,
        validators: [Validators.required],
      }
    ),
    similarityThreshold: new FormControl(DEFAULT_SIMILARITY_THRESHOLD, {
      nonNullable: true,
      validators: [Validators.required, Validators.min(0), Validators.max(1)],
    }),
    weights: new FormControl<VxKeyValuePair[]>([], {
      nonNullable: true,
      validators: [weightsValidator],
    }),
  });
}

/** Loads a stored configuration into the form without marking it dirty. */
export function patchAlertProcessorConfigurationForm(
  form: AlertProcessorConfigurationForm,
  configuration: AlertProcessorConfiguration
): void {
  const statistical = configuration.statisticalAlertCorrelator;
  form.reset({
    autoCreateIncidents: configuration.autoCreateIncidents,
    correlatorType: configuration.correlatorType,
    similarityThreshold:
      statistical?.similarityThreshold ?? DEFAULT_SIMILARITY_THRESHOLD,
    weights: Object.entries(statistical?.weightConfiguration ?? {}).map(
      ([key, value]) => ({ key, value: String(value) })
    ),
  });
}

export function toAlertProcessorConfigurationSpec(
  form: AlertProcessorConfigurationForm
): AlertProcessorConfigurationSpec {
  const value = form.getRawValue();
  const spec: AlertProcessorConfigurationSpec = {
    autoCreateIncidents: value.autoCreateIncidents,
    correlatorType: value.correlatorType,
  };
  if (value.correlatorType === AlertCorrelatorType.STATISTICAL) {
    spec.statisticalAlertCorrelatorSpec = {
      similarityThreshold: value.similarityThreshold,
      weightConfiguration: value.weights.reduce<Record<string, number>>(
        (acc, row) => ({ ...acc, [row.key.trim()]: Number(row.value) }),
        {}
      ),
    };
  }
  return spec;
}
